import React, { useState } from 'react';
import sendgridService from '../../services/sendgrid';
import organizationsService from '../../services/organizations';
import { useAuth } from "context/Auth";

const GeneralSettings: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, refreshUser } = useAuth();
  const [fromEmail, setFromEmail] = useState(user?.currentOrganization?.sendgridConfig?.from?.email || '');
  const [fromName, setFromName] = useState(user?.currentOrganization?.sendgridConfig?.from?.name || '');

  const handleUpdateFromEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await organizationsService.update(user?.currentOrganization?._id, {
        sendgridConfig: {
          ...user?.currentOrganization?.sendgridConfig,
          from: { email: fromEmail, name: fromName }
        }
      });
      await refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update from email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubuser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await sendgridService.createSubuser();
      await refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create subuser');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user?.currentOrganization?.sendgridConfig?.user_id) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
              {error}
            </div>
          )}
          <h2 className="text-xl font-semibold mb-4">Email Settings</h2>
          <p className="text-gray-600 mb-4">
            To enable email functionality for your organization, you need to create a SendGrid subuser account.
            This will allow you to send emails through the SendGrid platform.
          </p>
          <form onSubmit={handleCreateSubuser}>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`mt-2 px-4 py-2 rounded-md text-white font-medium ${isSubmitting
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : 'Enable Email'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          {error}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold">Email Configuration</h2>

          <div className="mt-6 mb-6">
            <h3 className="text-lg font-medium">From Email and Name</h3>
            <p className="text-sm text-gray-500">The email address that will be used to send emails from your organization.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex flex-col">
                <label htmlFor="fromEmail" className="text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  className="mt-2 w-full p-2 border border-gray-300 rounded-md"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="fromName" className="text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  className="mt-2 w-full p-2 border border-gray-300 rounded-md"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="mt-2 px-4 py-2 rounded-md text-white font-medium bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={handleUpdateFromEmail}
              >
                Update
              </button>
            </div>
          </div>
          <div className="mt-6 mb-6">
            <h3 className="text-lg font-medium mb-3">SendGrid Subuser Details</h3>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User ID:</p>
                  <p className="font-medium">{user?.currentOrganization?.sendgridConfig?.user_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email:</p>
                  <p className="font-medium">{user?.currentOrganization?.sendgridConfig?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Username:</p>
                  <p className="font-medium">{user?.currentOrganization?.sendgridConfig?.username}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-3">Usage Statistics</h3>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p className="mb-3">
                View detailed usage statistics in your SendGrid dashboard.
              </p>
              <a
                href="https://app.sendgrid.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Open SendGrid Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings; 